export type InvitationDocumentsPayload = {
  carpetasPaths: string[];
  documentosIds: number[];
};

/** Niveles de prefijo SharePoint que el árbol ignora al armar el value de cada carpeta. */
export const INVITATION_FOLDER_PREFIX_LEVELS = 7;

function parseStoredFolderPaths( raw: unknown ): string[] {
  if ( typeof raw === 'string' ) {
    const trimmed = raw.trim();
    if ( !trimmed ) return [];
    try {
      const parsed = JSON.parse( trimmed );
      if ( !Array.isArray( parsed ) ) return [];
      return parsed.filter( ( item ): item is string => typeof item === 'string' && item.trim() !== '' );
    } catch {
      return [];
    }
  }

  if ( Array.isArray( raw ) ) {
    return raw.filter( ( item ): item is string => typeof item === 'string' && item.trim() !== '' );
  }

  return [];
}

function decodeFolderPath( path: string ): string {
  try {
    return decodeURIComponent( path );
  } catch {
    return path;
  }
}

function folderPathToTreeValue( path: string, basePath: string ): string | null {
  const decoded = decodeFolderPath( path ).replace( /\/+$/, '' );
  if ( !decoded || /^\d+$/.test( decoded ) ) return null;

  const prefix = basePath.replace( /\/+$/, '' );
  if ( prefix && ( decoded === prefix || decoded.startsWith( `${prefix}/` ) ) ) {
    const relative = decoded === prefix ? '' : decoded.slice( prefix.length + 1 );
    return relative || null;
  }

  const parts = decoded.split( '/' ).filter( Boolean );
  if ( parts.length <= INVITATION_FOLDER_PREFIX_LEVELS ) return null;
  return parts.slice( INVITATION_FOLDER_PREFIX_LEVELS ).join( '/' );
}

export function storedFolderPathsToTreeValues( raw: unknown, basePath = '' ): string[] {
  return parseStoredFolderPaths( raw ).flatMap( ( path ) => {
    const relative = folderPathToTreeValue( path, basePath );
    return relative ? [relative] : [];
  } );
}

export function formatInvitationDocumentPaths(
  rawPaths: string[] | undefined,
  basePath: string,
): string[] {
  const paths = Array.isArray( rawPaths ) ? rawPaths : [];
  if ( !basePath ) return paths;

  return paths.map( ( path: string ) => (
    path.startsWith( `${basePath}/` ) ? path : `${basePath}/${path}`
  ) );
}

export function splitInvitationDocumentSelection(
  rawPaths: string[] | undefined,
  basePath: string,
): InvitationDocumentsPayload {
  const fullPaths = formatInvitationDocumentPaths( rawPaths, basePath );
  const documentosIds = fullPaths
    .filter( ( path ) => /^\d+$/.test( path ) )
    .map( ( id ) => Number( id ) );
  const carpetasPaths = fullPaths.filter( ( path ) => !/^\d+$/.test( path ) );

  return { carpetasPaths, documentosIds };
}
